import { ref } from "vue";

/**
 * "Add to Home Screen" hint for iOS (RDT-296).
 *
 * Android/Chrome offers an install prompt on its own; iOS Safari never does, so the only way to get
 * persolog Online onto the home screen there is the share sheet. This decides whether that hint is
 * worth showing — it is not, unless the trainer is on iOS Safari, is browsing (not already launched
 * from the home screen) and has not closed the hint before.
 */

/** localStorage flag: the hint has been closed and must not come back. */
export const IOS_INSTALL_HINT_DISMISSED_KEY = "iosInstallHintDismissed";

/**
 * iOS or iPadOS running Safari itself — the only combination with an "Add to Home Screen" entry in
 * the share sheet. Every iOS browser is WebKit underneath, so the engine says nothing; the user
 * agent's browser marker is what distinguishes them.
 */
export function isIosSafari(): boolean {
    const nav = globalThis.navigator;
    if (!nav) return false;

    const userAgent = nav.userAgent ?? "";
    // iPadOS 13+ reports itself as a Mac, so a touch-capable "MacIntel" is really an iPad.
    const isIos =
        /iPad|iPhone|iPod/.test(userAgent) || (nav.platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1);
    if (!isIos) return false;

    // Chrome, Firefox, Edge, Opera and in-app browsers all carry "Safari" in their user agent too,
    // so they have to be excluded explicitly.
    const isOtherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|YaBrowser|GSA\//.test(userAgent);
    return /Safari/.test(userAgent) && !isOtherBrowser;
}

/** Already running from the home screen (or as an installed PWA) ⇒ nothing left to explain. */
export function isStandalone(): boolean {
    const nav = globalThis.navigator as Navigator & { standalone?: boolean };
    if (nav?.standalone) return true;
    return globalThis.matchMedia?.("(display-mode: standalone)").matches ?? false;
}

/**
 * Has the trainer closed the hint before? Storage access is guarded because iOS Safari can refuse it
 * (private browsing, blocked cookies) — a hint is never worth breaking the page for.
 */
export function isIosInstallHintDismissed(): boolean {
    try {
        return localStorage.getItem(IOS_INSTALL_HINT_DISMISSED_KEY) === "true";
    } catch {
        return false;
    }
}

/** All conditions together: only then is the hint both relevant and wanted. */
export function shouldShowIosInstallHint(): boolean {
    return isIosSafari() && !isStandalone() && !isIosInstallHintDismissed();
}

export function useIosInstallHint() {
    // Decided once per page load: browser, display mode and the stored flag do not change while the
    // app is open, so there is nothing to watch.
    const visible = ref(shouldShowIosInstallHint());

    /** Close for good: the trainer either installed the app or is not interested. */
    function dismiss() {
        visible.value = false;
        try {
            localStorage.setItem(IOS_INSTALL_HINT_DISMISSED_KEY, "true");
        } catch {
            // Storage unavailable: the hint stays gone for this page load, which is the best we can do.
        }
    }

    return { visible, dismiss };
}
